import { City } from "@cryptors/api-client";
import { Box, TextField } from "@mui/material";
import { HttpError, IResourceComponentsProps } from "@refinedev/core";
import { Create } from "@refinedev/mui";
import { useForm } from "@refinedev/react-hook-form";
import { Controller } from "react-hook-form";

import {
  formatLaunchDate,
  formatQrfyIds,
  LaunchDateInput,
  parseLaunchDate,
  parseQrfyIds,
  QrfyIdsInput,
  validateLaunchDate,
  validateQrfyIds,
} from "./city-form";

interface CityFormValues {
  name: string;
  qrfyIds: QrfyIdsInput;
  launchDate: LaunchDateInput;
  location: {
    latitude: number;
    longitude: number;
  };
}

export const CitiesCreate: React.FC<IResourceComponentsProps> = () => {
  const {
    refineCore: { formLoading, onFinish },
    register,
    control,
    formState: { errors, isValid, isDirty },
    handleSubmit,
  } = useForm<City, HttpError, CityFormValues>();

  const onFinishHandler = (data: CityFormValues) => {
    onFinish({
      name: data.name,
      qrfyIds: parseQrfyIds(data.qrfyIds),
      launchDate: parseLaunchDate(data.launchDate),
      location: {
        latitude: data.location.latitude,
        longitude: data.location.longitude,
      },
    });
  };

  return (
    <Create
      isLoading={formLoading}
      saveButtonProps={{
        onClick: handleSubmit(onFinishHandler),
        disabled: !isDirty || !isValid,
      }}
    >
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <TextField
          {...register("name", {
            required: "Ce champ est requis",
          })}
          defaultValue={null}
          error={!!errors.name}
          helperText={errors.name?.message}
          margin="normal"
          fullWidth
          slotProps={{ inputLabel: { shrink: true } }}
          type="text"
          label="Nom"
          name="name"
        />
        <Controller
          control={control}
          name="qrfyIds"
          defaultValue=""
          rules={{ validate: validateQrfyIds }}
          render={({ field }) => (
            <TextField
              margin="normal"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              label="Qr codes associés (ids QRFY séparés par des virgules)"
              value={formatQrfyIds(field.value)}
              onChange={(event) => field.onChange(event.target.value)}
              error={!!errors.qrfyIds}
              helperText={errors.qrfyIds?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="launchDate"
          defaultValue=""
          rules={{ validate: validateLaunchDate }}
          render={({ field }) => (
            <TextField
              {...field}
              margin="normal"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              type="date"
              label="Date de lancement"
              value={formatLaunchDate(field.value)}
              error={!!errors.launchDate}
              helperText={errors.launchDate?.message}
            />
          )}
        />
        <TextField
          {...register("location.latitude", {
            required: "Ce champ est requis",
            valueAsNumber: true,
          })}
          defaultValue={null}
          error={!!errors.location?.latitude}
          helperText={errors.location?.latitude?.message}
          margin="normal"
          fullWidth
          slotProps={{ inputLabel: { shrink: true } }}
          type="number"
          label="Latitude"
          name="location.latitude"
        />
        <TextField
          {...register("location.longitude", {
            required: "Ce champ est requis",
            valueAsNumber: true,
          })}
          defaultValue={null}
          error={!!errors.location?.longitude}
          helperText={errors.location?.longitude?.message}
          margin="normal"
          fullWidth
          slotProps={{ inputLabel: { shrink: true } }}
          type="number"
          label="Longitude"
          name="location.longitude"
        />
      </Box>
    </Create>
  );
};
